/**
 * Dynamic Text Component
 *
 * Shared component for rendering text with variable replacements.
 * Numbers can be animated using NumberFlow when they change.
 */
import React, { type FC, useState, useEffect, useRef, type ReactNode } from 'react';
import NumberFlow from '@number-flow/react';
import { replaceStrings } from '../utils/replacers';

// ============================================================================
// Types
// ============================================================================

export interface DynamicTextProps {
	/**
	 * The text template with variables like {{credits}}, {{credits_currency}}
	 */
	text: string;

	/**
	 * Replacements for variables in the text
	 */
	replacements: Record<string, string | number>;

	/**
	 * Keys that should be animated (must be numeric values)
	 */
	animatedKeys?: string[];

	/**
	 * Whether to enable animations (default: true)
	 */
	enableAnimation?: boolean;

	/**
	 * Server-rendered initial HTML content (preserves initial render)
	 */
	initialContent?: string;

	/**
	 * Optional wrapper component or element type
	 */
	as?: keyof JSX.IntrinsicElements | React.ComponentType<{ children: ReactNode }>;

	/**
	 * Additional props for the wrapper element
	 */
	wrapperProps?: Record<string, unknown>;
}

interface ParsedSegment {
	type: 'text' | 'animated' | 'static';
	content: string;
	value?: number;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Parse text and identify segments that should be animated
 */
function parseTextSegments(
	text: string,
	replacements: Record<string, string | number>,
	animatedKeys: string[]
): ParsedSegment[] {
	const segments: ParsedSegment[] = [];
	let remainingText = text;
	let lastIndex = 0;

	// Create regex to match all variables
	const variableRegex = /{{\s*(\w+)\s*}}/g;
	let match: RegExpExecArray | null;

	// biome-ignore lint/suspicious/noAssignInExpressions: Standard regex pattern matching
	while ((match = variableRegex.exec(text)) !== null) {
		const [fullMatch, variableName] = match;
		const startIndex = match.index;

		// Add text before this variable
		if (startIndex > lastIndex) {
			segments.push({
				type: 'text',
				content: text.substring(lastIndex, startIndex),
			});
		}

		// Check if this variable should be animated
		const value = replacements[variableName];
		const shouldAnimate = animatedKeys.includes(variableName) && typeof value === 'number';

		if (shouldAnimate) {
			segments.push({
				type: 'animated',
				content: variableName,
				value: value as number,
			});
		} else {
			segments.push({
				type: 'static',
				content: String(value ?? ''),
			});
		}

		lastIndex = startIndex + fullMatch.length;
	}

	// Add remaining text
	if (lastIndex < text.length) {
		segments.push({
			type: 'text',
			content: text.substring(lastIndex),
		});
	}

	return segments;
}

// ============================================================================
// Component
// ============================================================================

/**
 * DynamicText Component
 *
 * Renders text with variable replacement and optional number animation.
 * Preserves server-rendered content to avoid flash during hydration.
 *
 * @example
 * ```tsx
 * <DynamicText
 *   text="You have {{credits}} {{credits_currency}}"
 *   replacements={{ credits: 500, credits_currency: 'points' }}
 *   animatedKeys={['credits']}
 *   enableAnimation={true}
 *   initialContent="You have 500 points"
 * />
 * ```
 */
export const DynamicText: FC<DynamicTextProps> = ({
	text,
	replacements,
	animatedKeys = [],
	enableAnimation = true,
	initialContent,
	as: Wrapper = 'span',
	wrapperProps = {},
}) => {
	// Track render count to prevent animation on first render
	// 0 = show initial content, 1 = hydrated (no animation), 2+ = animate
	const [renderCount, setRenderCount] = useState(0);
	const hasInitialContent = Boolean(initialContent);

	useEffect(() => {
		// Increment render count on each render
		setRenderCount((prev) => prev + 1);
	}, [replacements]);

	// Show initial server-rendered content first to prevent flash
	if (hasInitialContent && renderCount === 0) {
		return (
			<Wrapper {...wrapperProps}>
				{/* biome-ignore lint/security/noDangerouslySetInnerHtml: This is safe as we control the text content */}
				<span dangerouslySetInnerHTML={{ __html: initialContent || '' }} />
			</Wrapper>
		);
	}

	// If animation is disabled or no animated keys, use simple replacement
	if (!enableAnimation || animatedKeys.length === 0) {
		const replacementConfig = Object.entries(replacements).map(([key, value]) => ({
			[`{{${key}}}`]: String(value),
		}));

		const replacedText = replaceStrings(text, replacementConfig);

		return (
			<Wrapper {...wrapperProps}>
				{/* biome-ignore lint/security/noDangerouslySetInnerHtml: This is safe as we control the text content */}
				<span dangerouslySetInnerHTML={{ __html: replacedText }} />
			</Wrapper>
		);
	}

	// Parse text into segments for animation
	const segments = parseTextSegments(text, replacements, animatedKeys);

	// Only animate after the first render (prevents 0 → value animation)
	const shouldAnimate = enableAnimation && renderCount > 1;

	return (
		<Wrapper {...wrapperProps}>
			{segments.map((segment, index) => {
				if (segment.type === 'animated' && segment.value !== undefined) {

					return (
						<NumberFlow
							key={`${segment.content}-${index}`}
							value={segment.value}
							animated={shouldAnimate}
						/>
					);
				}

				if (segment.type === 'text' || segment.type === 'static') {
					return (
						<span
							key={`text-${index}`}
							// biome-ignore lint/security/noDangerouslySetInnerHtml: This is safe as we control the text content
							dangerouslySetInnerHTML={{ __html: segment.content }}
						/>
					);
				}

				return null;
			})}
		</Wrapper>
	);
};

export default DynamicText;
